import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { CookieBanner, CookieSettingsButton } from "./components/CookieBanner";
import ComingSoonPage from "./pages/ComingSoonPage";
import LandingPage from "./pages/LandingPage";
import WizardPage from "./pages/WizardPage";
import PreviewPage from "./pages/PreviewPage";
import PublishPage from "./pages/PublishPage";
import SuccessPage from "./pages/SuccessPage";
import GeneratedPage from "./pages/GeneratedPage";
import DashboardPage from "./pages/DashboardPage";
import PricingPage from "./pages/PricingPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import CheckoutPage from "./pages/CheckoutPage";
import AdminPage from "./pages/AdminPage";
import AuthPage from "./pages/AuthPage";
import SetPasswordPage from "./pages/SetPasswordPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import MyPagesPage from "./pages/MyPagesPage";
import ImpressumPage from "./pages/ImpressumPage";
import DatenschutzPage from "./pages/DatenschutzPage";
import AGBPage from "./pages/AGBPage";
import AVVPage from "./pages/AVVPage";
import WebsiteCheckPage from "./pages/WebsiteCheckPage";
import CustomDomainPage from "./pages/CustomDomainPage";

// Known hostnames that run the main WebGenerator app. Any other hostname is
// treated as a customer's own custom/connected domain, and gets served the
// single generated landing page (same component used by the preview).
function isMainAppHost() {
  const host = window.location.hostname.toLowerCase();
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith("webgenerator.io") ||
    host.endsWith(".vercel.app") ||
    host.endsWith(".emergentagent.com")
  );
}

function RefCapture() {
  const { search } = useLocation();
  useEffect(() => {
    const ref = new URLSearchParams(search).get("ref");
    if (ref && /^[A-Za-z0-9]{4,12}$/.test(ref)) localStorage.setItem("wg_ref", ref.toUpperCase());
  }, [search]);
  return null;
}

function CookieUI() {
  const { pathname } = useLocation();
  if (/^\/(page|s|vorschau)\//.test(pathname) || pathname.startsWith("/admin")) return null;
  return (
    <>
      <CookieBanner />
      {!pathname.startsWith("/dashboard") && <CookieSettingsButton />}
    </>
  );
}

// Coming-Soon-Modus: öffentliche Marketing-/Erstellungsseiten gesperrt; Umgehen per ?vorschau=intern
const COMING_SOON = true;
const GATED = /^\/($|assistent|vorschau|veroeffentlichen|kasse|preise|webseiten-check|wizard|pricing)/;

function Gate({ children }) {
  const { pathname, search } = useLocation();
  if (new URLSearchParams(search).get("vorschau") === "intern") localStorage.setItem("cs_bypass", "1");
  if (COMING_SOON && GATED.test(pathname) && !localStorage.getItem("cs_bypass")) return <ComingSoonPage />;
  return children;
}

function App() {
  useEffect(() => {
    const badge = document.getElementById("emergent-badge");
    if (badge) badge.remove();
  }, []);

  if (!isMainAppHost()) {
    return (
      <div className="min-h-screen">
        <CustomDomainPage />
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <AuthProvider>
        <BrowserRouter>
          <Gate>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/coming-soon" element={<ComingSoonPage />} />
            <Route path="/assistent" element={<WizardPage />} />
            <Route path="/vorschau/:pageId" element={<PreviewPage />} />
            <Route path="/veroeffentlichen/:pageId" element={<PublishPage />} />
            <Route path="/success/:pageId" element={<SuccessPage />} />
            <Route path="/page/:pageId" element={<GeneratedPage />} />
            <Route path="/dashboard/:pageId" element={<DashboardPage />} />
            <Route path="/preise" element={<PricingPage />} />
            <Route path="/kasse/:pageId" element={<CheckoutPage />} />
            <Route path="/zahlung-erfolgreich" element={<PaymentSuccessPage />} />
            {/* Redirects for old URLs */}
            <Route path="/payment-success" element={<Navigate to={`/zahlung-erfolgreich${window.location.search}`} replace />} />
            <Route path="/my-pages" element={<Navigate to="/meine-seiten" replace />} />
            <Route path="/auth" element={<Navigate to={`/anmelden${window.location.search}`} replace />} />
            <Route path="/wizard" element={<Navigate to="/assistent" replace />} />
            <Route path="/pricing" element={<Navigate to="/preise" replace />} />
            <Route path="/set-password" element={<Navigate to={`/passwort-setzen${window.location.search}`} replace />} />
            <Route path="/forgot-password" element={<Navigate to="/passwort-vergessen" replace />} />
            <Route path="/s/:subdomain" element={<GeneratedPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/anmelden" element={<AuthPage />} />
            <Route path="/passwort-setzen" element={<SetPasswordPage />} />
            <Route path="/passwort-vergessen" element={<ForgotPasswordPage />} />
            <Route path="/meine-seiten" element={<MyPagesPage />} />
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/datenschutz" element={<DatenschutzPage />} />
            <Route path="/agb" element={<AGBPage />} />
            <Route path="/avv" element={<AVVPage />} />
            <Route path="/webseiten-check" element={<WebsiteCheckPage />} />
          </Routes>
          </Gate>
          <RefCapture />
          <CookieUI />
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
